import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import authService from '../../auth/authService';
import SaktiRemoteInspectionService from '../services/saktiRemoteInspectionService';
import {
  CreateRemoteSessionRequest,
  CreateRemoteSessionResponse,
  RemoteQueryResult,
  RemoteSession,
  RemoteSessionLog,
  RemoteWSStatus,
  SchemaTable,
  WSMessage,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dev-sakti.gbs.id/api/insight';
const WS_BASE = API_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://');

function makeRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface PendingApprovalInfo {
  session_id: string;
  device_model: string;
  app_version: string;
  device_id: string;
}

interface UseSaktiRemoteInspectionResult {
  wsStatus: RemoteWSStatus;
  activeSession: CreateRemoteSessionResponse | null;
  pendingApproval: PendingApprovalInfo | null;
  sessionActive: boolean;
  schema: SchemaTable[];
  queryResult: RemoteQueryResult | null;
  queryLoading: boolean;
  sessions: RemoteSession[];
  sessionsTotal: number;
  sessionsLoading: boolean;
  sessionLogs: RemoteSessionLog[];
  sessionLogsLoading: boolean;
  createSession: (req: CreateRemoteSessionRequest) => Promise<CreateRemoteSessionResponse | null>;
  cancelSession: (sessionId: string) => Promise<boolean>;
  completeSession: (sessionId: string) => Promise<void>;
  connectWS: (sessionId: string) => void;
  disconnectWS: () => void;
  approveClient: () => void;
  rejectClient: () => void;
  sendSchemaRequest: () => void;
  sendQuery: (sql: string, args?: unknown[]) => void;
  sendDownload: () => void;
  sendRevert: (targetTable: string, rowId: string) => void;
  fetchSessions: (page?: number, status?: string) => Promise<void>;
  fetchSessionLogs: (sessionId: string) => Promise<void>;
}

export const useSaktiRemoteInspection = (): UseSaktiRemoteInspectionResult => {
  const toast = useToast();
  const wsRef = useRef<WebSocket | null>(null);
  // Tracks intentional close (complete/cancel) to suppress the unexpected-disconnect toast.
  const intentionalCloseRef = useRef(false);

  const [wsStatus, setWsStatus] = useState<RemoteWSStatus>('idle');
  const [activeSession, setActiveSession] = useState<CreateRemoteSessionResponse | null>(null);
  const [pendingApproval, setPendingApproval] = useState<PendingApprovalInfo | null>(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [schema, setSchema] = useState<SchemaTable[]>([]);
  const [queryResult, setQueryResult] = useState<RemoteQueryResult | null>(null);
  const [queryLoading, setQueryLoading] = useState(false);

  const [sessions, setSessions] = useState<RemoteSession[]>([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [sessionLogs, setSessionLogs] = useState<RemoteSessionLog[]>([]);
  const [sessionLogsLoading, setSessionLogsLoading] = useState(false);

  const createSession = useCallback(async (req: CreateRemoteSessionRequest) => {
    try {
      const result = await SaktiRemoteInspectionService.createSession(req);
      setActiveSession(result);
      return result;
    } catch (err) {
      toast({
        title: 'Gagal membuat sesi',
        description: err instanceof Error ? err.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return null;
    }
  }, [toast]);

  const disconnectWS = useCallback(() => {
    intentionalCloseRef.current = true;
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsStatus('closed');
  }, []);

  const cancelSession = useCallback(async (sessionId: string) => {
    try {
      const result = await SaktiRemoteInspectionService.cancelSession(sessionId);
      if (result.success) {
        toast({ title: 'Sesi dibatalkan', status: 'info', duration: 3000, isClosable: true });
        setActiveSession(null);
        setSessionActive(false);
        setSchema([]);
        setQueryResult(null);
        setQueryLoading(false);
        disconnectWS();
        return true;
      }
      return false;
    } catch (err) {
      toast({
        title: 'Gagal membatalkan sesi',
        description: err instanceof Error ? err.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return false;
    }
  }, [toast, disconnectWS]);

  const completeSession = useCallback(async (sessionId: string) => {
    intentionalCloseRef.current = true;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Send complete_session WS message — Go notifies Android then closes both connections.
      wsRef.current.send(JSON.stringify({ type: 'complete_session', request_id: makeRequestId(), payload: {} }));
      // Optimistically clear UI now — WS will close from server side and onclose will skip the
      // unexpected-disconnect toast because intentionalCloseRef is already true.
      setActiveSession(null);
      setPendingApproval(null);
      setSessionActive(false);
      setSchema([]);
      setQueryResult(null);
      setQueryLoading(false);
      toast({ title: 'Sesi selesai', description: 'Sesi telah berhasil diselesaikan.', status: 'success', duration: 4000, isClosable: true });
      disconnectWS();
    } else {
      // WS already closed — fall back to HTTP endpoint.
      try {
        await SaktiRemoteInspectionService.completeSession(sessionId);
        setActiveSession(null);
        setSessionActive(false);
        setSchema([]);
        setQueryResult(null);
        setQueryLoading(false);
        toast({ title: 'Sesi selesai', description: 'Sesi telah diselesaikan.', status: 'success', duration: 4000, isClosable: true });
      } catch (err) {
        toast({
          title: 'Gagal menyelesaikan sesi',
          description: err instanceof Error ? err.message : 'Terjadi kesalahan',
          status: 'error', duration: 4000, isClosable: true,
        });
      }
    }
  }, [toast, disconnectWS]);

  const handleIncoming = useCallback((msg: WSMessage) => {
    switch (msg.type) {
      case 'schema_response': {
        const tables = (msg.payload.tables as SchemaTable[]) ?? [];
        setSchema(tables);
        break;
      }
      case 'query_response': {
        setQueryLoading(false);
        const result: RemoteQueryResult = {
          columns: (msg.payload.columns as string[]) ?? [],
          rows: (msg.payload.rows as unknown[][]) ?? [],
          rows_affected: (msg.payload.rows_affected as number) ?? 0,
          duration_ms: (msg.payload.duration_ms as number) ?? 0,
          is_write: (msg.payload.is_write as boolean) ?? false,
        };
        setQueryResult(result);
        if (msg.payload.error) {
          toast({
            title: 'Query gagal',
            description: String(msg.payload.error),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
        break;
      }
      case 'approval_request': {
        setPendingApproval({
          session_id: msg.payload.session_id as string,
          device_model: (msg.payload.device_model as string) || 'Unknown Device',
          app_version: (msg.payload.app_version as string) || '-',
          device_id: (msg.payload.device_id as string) || '-',
        });
        break;
      }
      case 'session_status': {
        const status = msg.payload.status as string;
        if (status === 'active') {
          setPendingApproval(null);
          toast({
            title: 'Perangkat terhubung',
            description: 'Android client telah terkoneksi dan siap diinspeksi.',
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else if (status === 'completed') {
          intentionalCloseRef.current = true;
          setPendingApproval(null);
          setActiveSession(null);
          setSessionActive(false);
          setSchema([]);
          setQueryResult(null);
          setQueryLoading(false);
          toast({ title: 'Sesi selesai', description: 'Sesi telah berhasil diselesaikan.', status: 'success', duration: 4000, isClosable: true });
          disconnectWS();
        } else if (status === 'cancelled') {
          intentionalCloseRef.current = true;
          setPendingApproval(null);
          setActiveSession(null);
          setSessionActive(false);
          setSchema([]);
          setQueryResult(null);
          setQueryLoading(false);
          toast({ title: 'Sesi dibatalkan', status: 'info', duration: 3000, isClosable: true });
          disconnectWS();
        }
        break;
      }
      case 'error': {
        toast({
          title: 'Kesalahan dari perangkat',
          description: String(msg.payload.message ?? 'Unknown error'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        break;
      }
      default:
        break;
    }
  }, [toast, disconnectWS]);

  const connectWS = useCallback((sessionId: string) => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    setWsStatus('connecting');
    setSessionActive(false);
    setSchema([]);
    setQueryResult(null);
    setQueryLoading(false);
    const token = authService.getToken();
    const url = `${WS_BASE}/api/sakti/support/admin/${sessionId}${token ? `?token=${token}` : ''}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => setWsStatus('connected');
    ws.onmessage = (event) => {
      try {
        const msg: WSMessage = JSON.parse(event.data as string);
        handleIncoming(msg);
      } catch {
        // non-JSON frame — ignore
      }
    };
    ws.onerror = () => {
      setWsStatus('error');
      toast({
        title: 'Koneksi error',
        description: 'Gagal terhubung ke server inspeksi. Coba buat sesi baru.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    };
    ws.onclose = () => {
      setWsStatus('closed');
      wsRef.current = null;
      if (!intentionalCloseRef.current) {
        toast({
          title: 'Koneksi terputus',
          description: 'Sesi ditutup secara tidak terduga. Buat sesi baru jika diperlukan.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
      intentionalCloseRef.current = false;
    };
  }, [handleIncoming]);

  const sendWS = useCallback((msg: WSMessage) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      toast({
        title: 'Tidak terhubung',
        description: 'WebSocket belum terkoneksi.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  const approveClient = useCallback(() => {
    sendWS({ type: 'approve_session', request_id: makeRequestId(), payload: {} });
    setPendingApproval(null);
    setSessionActive(true);
  }, [sendWS]);

  const rejectClient = useCallback(() => {
    sendWS({ type: 'reject_session', request_id: makeRequestId(), payload: {} });
    setPendingApproval(null);
  }, [sendWS]);

  const sendSchemaRequest = useCallback(() => {
    sendWS({ type: 'schema_request', request_id: makeRequestId(), payload: {} });
  }, [sendWS]);

  const sendQuery = useCallback((sql: string, args: unknown[] = []) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast({ title: 'Tidak terhubung', description: 'WebSocket belum terkoneksi.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setQueryLoading(true);
    wsRef.current.send(JSON.stringify({ type: 'query_request', request_id: makeRequestId(), payload: { sql, args } }));
  }, [toast]);

  const sendDownload = useCallback(() => {
    sendWS({ type: 'download_request', request_id: makeRequestId(), payload: {} });
  }, [sendWS]);

  const sendRevert = useCallback((targetTable: string, rowId: string) => {
    sendWS({ type: 'revert_request', request_id: makeRequestId(), payload: { table: targetTable, row_id: rowId } });
  }, [sendWS]);

  const fetchSessions = useCallback(async (page = 1, status?: string) => {
    setSessionsLoading(true);
    try {
      const res = await SaktiRemoteInspectionService.getSessions(page, 20, status);
      setSessions(res.data);
      setSessionsTotal(res.pagination.total_items);
    } catch (err) {
      toast({
        title: 'Gagal memuat riwayat sesi',
        description: err instanceof Error ? err.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSessionsLoading(false);
    }
  }, [toast]);

  const fetchSessionLogs = useCallback(async (sessionId: string) => {
    setSessionLogsLoading(true);
    try {
      const res = await SaktiRemoteInspectionService.getSessionLogs(sessionId);
      setSessionLogs(res.logs);
    } catch (err) {
      toast({
        title: 'Gagal memuat log sesi',
        description: err instanceof Error ? err.message : 'Terjadi kesalahan',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSessionLogsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSessions();
    return () => { wsRef.current?.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    wsStatus, activeSession, pendingApproval, sessionActive, schema, queryResult, queryLoading,
    sessions, sessionsTotal, sessionsLoading,
    sessionLogs, sessionLogsLoading,
    createSession, cancelSession, completeSession, connectWS, disconnectWS,
    approveClient, rejectClient,
    sendSchemaRequest, sendQuery, sendDownload, sendRevert,
    fetchSessions, fetchSessionLogs,
  };
};

export default useSaktiRemoteInspection;
